import { Component, Input, OnInit } from '@angular/core';
import { LsqApiServiceListSource, LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import { SupplierDetailsRequest } from '@lsq-frontend/remote-services/analytics-graphql/src/lib/enrollment-details/enrollment-details';
import { BehaviorSubject, of } from 'rxjs';

export interface ChartTableItem {
    companyName: string;
    enrolledDate: string;
    invoiceTermsAvg: string;
    invoiceAmount: number;
}

@Component({
    selector: 'lsq-chart-supplier-details',
    styleUrls: ['./lsq-chart-supplier-details.scss'],
    template: `
        <div class="container-fluid">
            <lsq-data-table
                [dataSource]="dataSource"
                [isPaginated]="true"
                [layoutFixed]="true"
                class="container-fluid"
            >
                <lsq-column
                    [column]="{
                        header: 'Supplier Company Name',
                        key: 'companyName',
                        truncateTextCss: true,
                        sortable: true,
                        width: '40%'
                    }"
                >
                </lsq-column>

                <lsq-column
                    [column]="{
                        header: 'Enrolled Date',
                        key: 'enrolledDate',
                        sortable: true,
                        width: '30%'
                    }"
                >
                </lsq-column>

                <lsq-column
                    [column]="{
                        header: 'Avg. Invoice Terms',
                        key: 'invoiceTermsAvg',
                        sortable: true
                    }"
                    [alignRight]="true"
                >
                </lsq-column>

                <lsq-column
                    [column]="{
                        header: 'Invoice Amount',
                        key: 'invoiceAmount',
                        sortable: true
                    }"
                    [alignRight]="true"
                >
                    <ng-template [custom-cell] let-row="row">
                        $ {{ row.invoiceAmount / 100 || 0 | number : '1.0-0' }}
                    </ng-template>
                </lsq-column>
            </lsq-data-table>
        </div>
    `
})
export class ChartSupplierDetailsComponent implements OnInit {
    @Input() public items: ChartTableItem[] = [];

    public defaultTake = 10;
    public dataSource: LsqListSource<ChartTableItem>;
    public currentTableData = new BehaviorSubject<ChartTableItem[]>([]);
    public skip = new BehaviorSubject<number>(0);
    public take = new BehaviorSubject<number>(this.defaultTake);

    public handleSkipChange(skip: number) {
        const take = this.dataSource.take;
        const refreshedItems = this.items.slice(skip, skip + take);
        this.currentTableData.next(refreshedItems);
        this.dataSource.skip = skip;
    }

    public handleSort(sorting: { sortColumn: string; sortDirection: number }) {
        const sortColumn = sorting.sortColumn || this.dataSource.sortColumn;
        const sortDirection = sorting.sortDirection || this.dataSource.sortDirection;
        let refreshedItems = this.items.sort((a: any, b: any) => {
            if (sortColumn === 'companyName') {
                return b[sortColumn].localeCompare(a[sortColumn]);
            } else if (sortColumn === 'enrolledDate') {
                const timeB = new Date(b[sortColumn]).getTime() || 0;
                const timeA = new Date(a[sortColumn]).getTime() || 0;
                return timeB - timeA;
            }
            return b[sortColumn] - a[sortColumn];
        });
        if (sortDirection === -1) {
            refreshedItems = refreshedItems.reverse();
        }
        this.currentTableData.next(refreshedItems);
        this.handleSkipChange(0);
        this.dataSource.sortColumn = sortColumn;
        this.dataSource.sortDirection = sortDirection;
    }

    public ngOnInit(): void {
        this.currentTableData.next(this.items.slice(0, this.defaultTake));
        this.dataSource = new LsqListSource<ChartTableItem>({
            rows$: this.currentTableData,
            totalCount$: of(this.items.length),
            take: this.take.value,
            skip: this.skip.value,
            handleTakeChange: (take: number) => {
                const refreshedItems = this.items.slice(0, take);
                this.currentTableData.next(refreshedItems);
                this.dataSource.take = take;
            },
            handleSkipChange: (skip: number) => this.handleSkipChange(skip),
            handleSort: (sorting: { sortColumn: string; sortDirection: number }) =>
                this.handleSort(sorting)
        });
    }
}
